// Role-based access control configuration

export const ROLES = {
  OWNER: 'owner',
  SUPER_ADMIN: 'super_admin', // Legacy alias for owner
  MANAGEMENT: 'management',
  ADMIN: 'admin',
  APPROVER: 'approver',
  HR: 'hr',
  EXTERNAL_CRM: 'external_crm',
  INTERNAL_TICKET_SUPPORT: 'internal_ticket_support',
  FINANCE: 'finance',
};

export const ROLE_LABELS = {
  [ROLES.OWNER]: 'Owner',
  [ROLES.SUPER_ADMIN]: 'Owner',
  [ROLES.MANAGEMENT]: 'Management',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.APPROVER]: 'Approver',
  [ROLES.HR]: 'HR',
  [ROLES.EXTERNAL_CRM]: 'External CRM',
  [ROLES.INTERNAL_TICKET_SUPPORT]: 'Internal Ticket Support',
  [ROLES.FINANCE]: 'Finance',
};

// Normalize role - treat super_admin as owner
const normalizeRole = (role) => {
  if (role === 'super_admin') return 'owner';
  return role;
};

// Define menu items and which roles can access them
export const MENU_PERMISSIONS = {
  dashboard: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
  employees: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.HR],
  tasks: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
  timesheets: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
  'password-requests': [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.HR],
  users: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.ADMIN],
  groups: [ROLES.OWNER, ROLES.SUPER_ADMIN], // Owner only
  events: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN],
  matches: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN],
  payments: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.FINANCE],
  invoices: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.FINANCE],
  tickets: [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.INTERNAL_TICKET_SUPPORT],
  'customer-support': [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.EXTERNAL_CRM],
  'monthly-attendance': [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.HR],
  'salary-calculation': [ROLES.OWNER, ROLES.SUPER_ADMIN, ROLES.HR],
};

// Define which roles have read-only access to certain menus
export const READ_ONLY_ACCESS = {
  [ROLES.MANAGEMENT]: ['employees', 'tasks', 'timesheets', 'events', 'matches', 'payments', 'invoices', 'tickets'],
};

// Check if a role can access a menu
export const canAccessMenu = (role, menuKey) => {
  const allowedRoles = MENU_PERMISSIONS[menuKey] || [];
  // Check both the original role and normalized role
  return allowedRoles.includes(role) || allowedRoles.includes(normalizeRole(role));
};

// Check if a role has read-only access to a menu
export const isReadOnly = (role, menuKey) => {
  const normalizedRole = normalizeRole(role);
  const readOnlyMenus = READ_ONLY_ACCESS[normalizedRole] || READ_ONLY_ACCESS[role] || [];
  return readOnlyMenus.includes(menuKey);
};

// Get all accessible menus for a role
export const getAccessibleMenus = (role) => {
  return Object.keys(MENU_PERMISSIONS).filter(menuKey => canAccessMenu(role, menuKey));
};

// Roles that can assign other roles
export const ROLE_ASSIGNMENT_PERMISSIONS = {
  [ROLES.OWNER]: [ROLES.OWNER, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.APPROVER, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
  [ROLES.SUPER_ADMIN]: [ROLES.OWNER, ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.APPROVER, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
  [ROLES.HR]: [ROLES.MANAGEMENT, ROLES.ADMIN, ROLES.APPROVER, ROLES.HR, ROLES.EXTERNAL_CRM, ROLES.INTERNAL_TICKET_SUPPORT, ROLES.FINANCE],
};

// Get assignable roles for a user
export const getAssignableRoles = (currentUserRole) => {
  const normalizedRole = normalizeRole(currentUserRole);
  return ROLE_ASSIGNMENT_PERMISSIONS[normalizedRole] || ROLE_ASSIGNMENT_PERMISSIONS[currentUserRole] || [];
};
